import { useHeaderHeight } from '@react-navigation/elements';
import { Banner, Button, Headline3, Meta, Spinner } from 'aimo-ui';
import BenefitCard from 'components/account/benefits/BenefitCard';
import Icon from 'components/icons/Icon';
import AccountContext from 'context/AccountContext';
import { useEmailVerifiedByOther } from 'hooks/useEmailVerifiedByOther';
import { usePermitsAndBenefits } from 'hooks/usePermitsAndBenefits';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, View } from 'react-native';
import { useBenefitStore } from 'stores/benefitStore';
import {
  ScreenContainer,
  ScreenWrapper,
  SheetDetailsWrapper,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { isWeb } from 'utils/deviceUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useUiStore } from 'stores/uiStore';
const PlusIcon: React.FC = () => <Icon name="plus" size="m" color="white" />;
const WarningIcon = () => <Icon name="Info-Outlined" />;

const BenefitsScreen: React.FC = () => {
  const headerHeight = useHeaderHeight();
  const { loading, customer } = useContext(AccountContext);
  const { t } = useTranslation();
  const customerUid = customer.uid || '';
  const email = customer.contactEmail || '';
  const { setSelectedBenefit } = useBenefitStore((state) => state);
  const { benefits } = usePermitsAndBenefits({
    customerUid: customerUid || undefined,
  });
  const { isVerifiedByOther } = useEmailVerifiedByOther({
    email,
    customerUid,
  });
  const hasBenefits = benefits && benefits.length > 0;
  const connectBenefit = () => {
    useUiStore.getState().setSheetModal('connectBenefit');
  };

  if (loading) {
    return (
      <ScreenContainer>
        <ScreenWrapper tabBarHeight={headerHeight}>
          <StyledWrapper center>
            <Spinner testID="my-vehicles-loader" />
          </StyledWrapper>
        </ScreenWrapper>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer mt={isWeb ? 30 : 0}>
      <SheetDetailsWrapper
        pt={isWeb ? 60 : 30}
        pb={20}
        style={{ justifyContent: 'space-between' }}>
        <View style={{ flex: 1 }}>
          <StyledWrapper mb={10}>
            <Headline3 {...testIdentifiers('benefits-title')}>
              {t('account.benefits')}
            </Headline3>
          </StyledWrapper>
          {hasBenefits ? (
            <ScrollView showsVerticalScrollIndicator={false}>
              {benefits.map((benefit, index) => (
                <BenefitCard
                  key={benefit.uid ?? index}
                  benefit={benefit}
                  index={index}
                  onPress={() => {
                    setSelectedBenefit(benefit);
                    useUiStore.getState().setSheetModal('benefitDetail');
                  }}
                />
              ))}
            </ScrollView>
          ) : (
            <StyledWrapper mb={0}>
              <Meta>{t('account.noBenefits')}</Meta>
            </StyledWrapper>
          )}
          {isVerifiedByOther && email && (
            <StyledWrapper mt={16} mb={0}>
              <Banner
                type="warning"
                text={t('account.emailVerifiedByOther', { email })}
                iconComponent={WarningIcon}
              />
            </StyledWrapper>
          )}
        </View>
        <StyledWrapper mb={0}>
          <Button
            loading={loading}
            testID="connect-benefit-button"
            size="l"
            iconComponent={PlusIcon}
            text={t('account.connectBenefit')}
            onPress={connectBenefit}
          />
        </StyledWrapper>
      </SheetDetailsWrapper>
    </ScreenContainer>
  );
};

export default BenefitsScreen;
